// Table — the one reusable data-table surface for BeneCRM v2.
//
// Consolidates the ~28 hand-rolled feature tables (FE-SCRM-038 audit) that
// drifted into 6 near-identical designs. Each drifted table was some ad-hoc
// combination of four axes; those axes are now props:
//
//   density   "sm" | "xs"   text size + cell padding      (default "sm")
//   layout    "auto"|"fixed"  table-auto vs table-fixed    (default "auto")
//   dividers  boolean       row dividers on <tbody>        (default false)
//   ruled     boolean       border-collapse + cell borders (default false)
//   boxed     boolean       rounded card chrome            (default false)
//
// The <table> is ALWAYS wrapped in an ``overflow-x-auto`` div — that is the
// FE-SCRM-038 acceptance criterion (no clipped columns; a scrollbar instead),
// so every consumer gets it for free and can drop the manual wrapper.
//
// Compositional primitives (not data-driven) so each feature keeps its bespoke
// cell content — badges, buttons, links, sort headers — while the chrome is
// enforced here. A React context propagates the axes to the sub-parts.

import {
  createContext,
  forwardRef,
  useContext,
  type HTMLAttributes,
  type ReactNode,
  type TableHTMLAttributes,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
} from "react";

import { cn } from "./cn";

export type TableDensity = "sm" | "xs";
export type TableLayout = "auto" | "fixed";
export type TableCellPadding = "normal" | "tight";

interface TableContextValue {
  cellPadding: TableCellPadding;
  ruled: boolean;
  dividers: boolean;
}

const TableContext = createContext<TableContextValue>({
  cellPadding: "normal",
  ruled: false,
  dividers: false,
});

const DENSITY_TEXT: Record<TableDensity, string> = {
  sm: "text-sm",
  xs: "text-xs",
};

// Cell padding is INDEPENDENT of text density: a table can be text-xs with
// roomy padding (RecentImports) or text-xs with tight padding (report tiles).
const CELL_PADDING: Record<TableCellPadding, string> = {
  normal: "px-3 py-2",
  tight: "px-2 py-1.5",
};

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  density?: TableDensity;
  layout?: TableLayout;
  /** Cell padding, independent of text density. Default "normal" (px-3 py-2). */
  cellPadding?: TableCellPadding;
  dividers?: boolean;
  ruled?: boolean;
  boxed?: boolean;
  /**
   * Whether to wrap the table in the ``overflow-x-auto`` scroller. Default
   * true. Set false ONLY for tables that manage their own layout and would
   * be broken by a new scroll container — e.g. ``table-fixed`` + ``colgroup``
   * grids with ``position: sticky`` headers, where an overflow ancestor
   * reparents the sticky context. With ``scroll={false}`` the component
   * renders a bare ``<table>`` (no wrapper) and ``boxed`` / ``wrapperClassName``
   * are ignored — the call-site owns the surrounding box.
   */
  scroll?: boolean;
  /** Extra classes on the outer overflow wrapper (e.g. a min-width floor). */
  wrapperClassName?: string;
  children?: ReactNode;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  {
    density = "sm",
    layout = "auto",
    cellPadding = "normal",
    dividers = false,
    ruled = false,
    boxed = false,
    scroll = true,
    className,
    wrapperClassName,
    children,
    ...rest
  },
  ref,
) {
  const table = (
    <table
      ref={ref}
      data-density={density}
      data-layout={layout}
      data-ruled={ruled ? "" : undefined}
      data-dividers={dividers ? "" : undefined}
      className={cn(
        "w-full text-left",
        DENSITY_TEXT[density],
        layout === "fixed" ? "table-fixed" : "table-auto",
        ruled && "border-collapse",
        className,
      )}
      {...rest}
    >
      <TableContext.Provider value={{ cellPadding, ruled, dividers }}>
        {children}
      </TableContext.Provider>
    </table>
  );

  // Self-managed layout (sticky/colgroup grids): no wrapper, no new scroll
  // context. The call-site keeps its own surrounding box.
  if (!scroll) return table;

  return (
    <div
      // overflow-x-auto is the FE-SCRM-038 defensive plug — never remove it.
      className={cn(
        "overflow-x-auto",
        boxed && "rounded-lg border border-border-subtle bg-raised",
        wrapperClassName,
      )}
    >
      {table}
    </div>
  );
});

export interface THeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  children?: ReactNode;
}

export const THead = forwardRef<HTMLTableSectionElement, THeadProps>(
  function THead({ className, children, ...rest }, ref) {
    // Intentionally style-neutral: header background / border / text colour
    // come from each call-site's className so migrations preserve their exact
    // look with no default-vs-caller CSS-order conflicts. Cell weight/padding
    // live on <Th>.
    return (
      <thead ref={ref} className={cn(className)} {...rest}>
        {children}
      </thead>
    );
  },
);

export interface TBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children?: ReactNode;
}

export const TBody = forwardRef<HTMLTableSectionElement, TBodyProps>(
  function TBody({ className, children, ...rest }, ref) {
    const { dividers } = useContext(TableContext);
    return (
      <tbody
        ref={ref}
        className={cn(
          dividers && "divide-y divide-border-subtle",
          className,
        )}
        {...rest}
      >
        {children}
      </tbody>
    );
  },
);

export interface TrProps extends HTMLAttributes<HTMLTableRowElement> {
  children?: ReactNode;
}

export const Tr = forwardRef<HTMLTableRowElement, TrProps>(function Tr(
  { className, children, ...rest },
  ref,
) {
  return (
    <tr ref={ref} className={cn(className)} {...rest}>
      {children}
    </tr>
  );
});

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
}

export const Th = forwardRef<HTMLTableCellElement, ThProps>(function Th(
  { className, children, ...rest },
  ref,
) {
  const { cellPadding, ruled } = useContext(TableContext);
  return (
    <th
      ref={ref}
      scope="col"
      className={cn(
        CELL_PADDING[cellPadding],
        "text-left align-middle font-medium",
        ruled && "border border-border-subtle",
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
});

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
}

export const Td = forwardRef<HTMLTableCellElement, TdProps>(function Td(
  { className, children, ...rest },
  ref,
) {
  const { cellPadding, ruled } = useContext(TableContext);
  return (
    <td
      ref={ref}
      className={cn(
        CELL_PADDING[cellPadding],
        "align-middle",
        ruled && "border border-border-subtle",
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
});
